/**
 * Web Notification（TUINotification）接入：
 * IM 登录成功后监听 newMessageList，页面未聚焦时弹出浏览器系统通知。
 * 配合 App 端 TIMPush：发送侧已带 offlinePushInfo（见 OfflinePushInfoManager）。
 */
import eventBus from '@/utils/eventBus'

let started = false
/** 防止 loginIM / 消息中心并发调用时重复 watch */
let startingPromise = null
let unwatchNewMessageList = null
let unbindElectronNotificationClick = null

/** 短时间去重，避免同一条消息被多次回调 */
const recentNotifiedIds = new Set()
const NOTIFY_DEDUP_MS = 8000
const NOTIFY_DEDUP_MAX = 200

const DEFAULT_TITLE = '晨多陪玩'
const DEFAULT_ICON = 'https://web.sdk.qcloud.com/im/demo/latest/faviconnew.png'

function isElectronEnv() {
  return !!(typeof window !== 'undefined' && window.cdElectron && window.cdElectron.isElectron)
}

function bindElectronNotificationClick() {
  if (!isElectronEnv() || typeof window.cdElectron.onNotificationClick !== 'function') return
  if (typeof unbindElectronNotificationClick === 'function') {
    try { unbindElectronNotificationClick() } catch (_) { /* ignore */ }
  }
  unbindElectronNotificationClick = window.cdElectron.onNotificationClick(async (payload) => {
    try {
      openMessageCenterFromNotification()
    } catch (_) {
      // ignore
    }
    const conversationID = payload && payload.conversationID
    if (!conversationID) return
    try {
      const { TUIConversationService } = await import('@tencentcloud/chat-uikit-engine-lite')
      setTimeout(async () => {
        try {
          const conversationProfile = await TUIConversationService.getConversationProfile(conversationID)
          const id = conversationProfile?.data?.conversation?.conversationID
          if (id) TUIConversationService.switchConversation(id)
        } catch (_) {
          // ignore
        }
      }, 400)
    } catch (_) {
      // ignore
    }
  })
}

function rememberNotified(messageId) {
  if (!messageId) return false
  const id = String(messageId)
  if (recentNotifiedIds.has(id)) return true
  recentNotifiedIds.add(id)
  if (recentNotifiedIds.size > NOTIFY_DEDUP_MAX) {
    recentNotifiedIds.clear()
  }
  setTimeout(() => {
    recentNotifiedIds.delete(id)
  }, NOTIFY_DEDUP_MS)
  return false
}

function isOutgoingMessage(message) {
  if (!message) return false
  // IM SDK：out=自己发出，in=收到
  if (message.flow === 'out') return true
  if (typeof message.isMine === 'boolean' && message.isMine) return true
  try {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null') || {}
    const myId = String(userInfo.userID || userInfo.userId || userInfo.id || userInfo.uid || '').trim()
    const from = String(message.from || message.sender || '').trim()
    if (myId && from && myId === from) return true
  } catch (_) {
    // ignore
  }
  return false
}

function applyNotificationConfig(TUINotification, options = {}) {
  TUINotification.setNotificationConfiguration({
    showPreviews: options.showPreviews !== false,
    allowNotifications: options.allowNotifications !== false,
    notificationTitle: options.title || DEFAULT_TITLE,
    notificationIcon: options.icon || DEFAULT_ICON,
  })
}

/**
 * 初始化并开启 Web 消息通知
 * @param {{ title?: string, icon?: string, showPreviews?: boolean, allowNotifications?: boolean, requestPermission?: boolean }} [options]
 */
export async function startIMWebNotification(options = {}) {
  if (typeof window === 'undefined') return
  // Electron 走主进程原生通知；浏览器才需要 Notification API
  if (!isElectronEnv() && !('Notification' in window)) {
    console.warn('[IM Notification] 当前环境不支持 Web Notification')
    return
  }

  // 已挂好监听：只更新配置，不再二次 watch（修复并发注册导致一条消息弹多条通知）
  if (started && unwatchNewMessageList) {
    try {
      const mod = await import('@/TUIKit/components/TUINotification/index')
      const TUINotification = mod && (mod.default || mod)
      if (TUINotification && typeof TUINotification.setNotificationConfiguration === 'function') {
        applyNotificationConfig(TUINotification, options)
        if (options.requestPermission && typeof TUINotification.requestNotificationPermission === 'function') {
          await TUINotification.requestNotificationPermission()
        }
      }
    } catch (_) {
      // ignore
    }
    return
  }

  // 并发调用共用同一个启动 Promise
  if (startingPromise) {
    await startingPromise
    if (started && unwatchNewMessageList) {
      try {
        const mod = await import('@/TUIKit/components/TUINotification/index')
        const TUINotification = mod && (mod.default || mod)
        if (TUINotification && typeof TUINotification.setNotificationConfiguration === 'function') {
          applyNotificationConfig(TUINotification, options)
        }
      } catch (_) {
        // ignore
      }
    }
    return
  }

  startingPromise = (async () => {
    const [{ TUIStore, StoreName }, TUINotificationMod] = await Promise.all([
      import('@tencentcloud/chat-uikit-engine-lite'),
      import('@/TUIKit/components/TUINotification/index'),
    ])
    const TUINotification = TUINotificationMod && (TUINotificationMod.default || TUINotificationMod)
    if (!TUINotification || typeof TUINotification.setNotificationConfiguration !== 'function') {
      console.warn('[IM Notification] TUINotification 加载失败')
      return
    }

    applyNotificationConfig(TUINotification, options)

    // Electron 无需浏览器权限；Web 在用户手势场景再申请
    if (!isElectronEnv() && options.requestPermission !== false && typeof TUINotification.requestNotificationPermission === 'function') {
      try {
        await TUINotification.requestNotificationPermission()
      } catch (_) {
        // ignore
      }
    }

    bindElectronNotificationClick()

    // await 后再检查一次，避免竞态重复 watch
    if (started && unwatchNewMessageList) {
      return
    }

    unwatchNewMessageList = TUIStore.watch(StoreName.CHAT, {
      newMessageList: (newMessageList) => {
        if (!Array.isArray(newMessageList) || !newMessageList.length) return
        newMessageList.forEach((message) => {
          try {
            // 不推送自己发出的消息
            if (isOutgoingMessage(message)) return
            // 同一 message.ID 短时间只推一次
            if (rememberNotified(message.ID || message.id || message.messageID)) return
            TUINotification.notify(message)
          } catch (e) {
            console.warn('[IM Notification] notify failed:', e)
          }
        })
      },
    })
    started = true
  })()

  try {
    await startingPromise
  } finally {
    startingPromise = null
  }
}

/** 关闭 Web 消息通知监听（退出登录时调用） */
export function stopIMWebNotification() {
  if (typeof unwatchNewMessageList === 'function') {
    try {
      unwatchNewMessageList()
    } catch (_) {
      // ignore
    }
  }
  unwatchNewMessageList = null
  if (typeof unbindElectronNotificationClick === 'function') {
    try {
      unbindElectronNotificationClick()
    } catch (_) {
      // ignore
    }
  }
  unbindElectronNotificationClick = null
  started = false
  startingPromise = null
  recentNotifiedIds.clear()

  import('@/TUIKit/components/TUINotification/index')
    .then((mod) => {
      const TUINotification = mod && (mod.default || mod)
      if (TUINotification && typeof TUINotification.setNotificationConfiguration === 'function') {
        TUINotification.setNotificationConfiguration({ allowNotifications: false })
      }
    })
    .catch(() => {})
}

/**
 * 在用户手势下申请通知权限（如打开消息中心时）
 */
export async function requestIMNotificationPermission() {
  if (typeof window === 'undefined') return false
  if (isElectronEnv()) return true
  if (!('Notification' in window)) return false
  try {
    const mod = await import('@/TUIKit/components/TUINotification/index')
    const TUINotification = mod && (mod.default || mod)
    if (!TUINotification || typeof TUINotification.requestNotificationPermission !== 'function') {
      return false
    }
    TUINotification.setNotificationConfiguration({ allowNotifications: true })
    return await TUINotification.requestNotificationPermission()
  } catch (_) {
    return false
  }
}

/**
 * 点击系统通知后：聚焦页面 + 打开消息中心
 */
export function openMessageCenterFromNotification() {
  try {
    if (typeof window !== 'undefined' && typeof window.focus === 'function') {
      window.focus()
    }
  } catch (_) {
    // ignore
  }
  if (eventBus && eventBus.$emit) {
    eventBus.$emit('open-message-center')
  }
}
